import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Autocomplete,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
} from '@mui/material';
import {
    CloudUpload,
    Delete,
    Wallpaper,
    Label,
    Search,
    PhotoLibrary,
    Close,
    Add,
    ExpandMore,
    DragIndicator,
} from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';
import { SCUIButton, SCUIZoomableImage } from '../../../atomic/atoms';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { BackgroundLabelCommand } from '../../../services/studio/StudioCommand';
import {
    BackgroundLabelQuery,
    BackgroundLabelItem,
} from '../../../services/studio/StudioQuery/BackgroundLabel/BackgroundLabelQuery.services';
import Swal from 'sweetalert2';
import './BackgroundLabelManagement.scss';

// Drag item type - kategori bazında unique
const DRAG_TYPE = 'BACKGROUND_LABEL_ITEM';

interface DragItemType {
    id: string;
    index: number;
    category: string;
}

interface DraggableGridItemProps {
    item: BackgroundLabelItem;
    index: number;
    category: string;
    moveItem: (category: string, fromIndex: number, toIndex: number) => void;
    dropItem: (category: string) => void;
    onDelete: (item: BackgroundLabelItem) => void;
    formatDate: (dateStr: string) => string;
    formatFileSize: (bytes: number) => string;
}

const DraggableGridItem = ({
    item,
    index,
    category,
    moveItem,
    dropItem,
    onDelete,
    formatDate,
    formatFileSize,
}: DraggableGridItemProps) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag] = useDrag({
        type: DRAG_TYPE,
        item: { id: item.id, index, category },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: DRAG_TYPE,
        canDrop: (dragItem: DragItemType) => dragItem.category === category,
        hover: (dragItem: DragItemType, monitor) => {
            if (!ref.current) return;
            if (dragItem.category !== category) return;

            const dragIndex = dragItem.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveItem(category, dragIndex, hoverIndex);
            dragItem.index = hoverIndex;
        },
        drop: (dragItem: DragItemType) => {
            if (dragItem.category === category) {
                dropItem(category);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver() && monitor.canDrop(),
        }),
    });

    // ref'i hem drag hem drop için bağla
    drag(drop(ref));

    return (
        <Grid
            size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 2,
            }}
        >
            <div ref={ref} style={{ height: '100%' }}>
                <Card
                    className={`background-label-management__grid-item ${isOver ? 'background-label-management__grid-item--drop-target' : ''}`}
                    elevation={2}
                    sx={{
                        opacity: isDragging ? 0.4 : 1,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        transition: 'opacity 0.2s, box-shadow 0.2s',
                        height: '100%',
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <DragIndicator fontSize="small" sx={{ color: '#aaa' }} />
                            <Chip
                                label={`#${item.displayOrder || index + 1}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                        </Box>
                        <Box className="background-label-management__item-avatar">
                            <SCUIZoomableImage
                                src={item.s3Url}
                                alt={item.name}
                                containerSx={{
                                    width: '100%',
                                    height: 150,
                                    border: '2px solid #eee',
                                    borderRadius: 1,
                                }}
                                imageSx={{
                                    borderRadius: 1,
                                }}
                            />
                        </Box>
                        <Box className="background-label-management__item-info" sx={{ mt: 1 }}>
                            <Tooltip title={item.name}>
                                <Typography variant="subtitle2" noWrap sx={{ maxWidth: '100%' }}>
                                    {item.name}
                                </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {formatDate(item.uploadDate)}
                            </Typography>
                            <Chip label={item.uploadedBy} size="small" sx={{ mt: 0.5, fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {formatFileSize(item.fileSize)}
                            </Typography>
                        </Box>
                        <Box
                            className="background-label-management__item-actions"
                            sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'center' }}
                        >
                            <Tooltip title={t('general.delete')}>
                                <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        </Grid>
    );
};

// Enums matching backend
const BackgroundLabelType = { Background: 1, Label: 2 } as const;
const BackgroundLabelScope = { Personal: 1, Global: 2 } as const;

export const BackgroundLabelManagement = () => {
    const { t } = useTranslation();

    // State
    const [activeTab, setActiveTab] = useState(0); // 0=Background, 1=Label
    const [groupedItems, setGroupedItems] = useState<{ [key: string]: BackgroundLabelItem[] }>({});
    const [categories, setCategories] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadName, setUploadName] = useState('');
    const [uploadCategory, setUploadCategory] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFilePreview, setSelectedFilePreview] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Önerilen kategori seçenekleri
    const categorySuggestions = ['ERKEK', 'KADIN', 'ÇOCUK', 'BEBEK', 'HOME', 'FON'];

    const currentType = activeTab === 0 ? BackgroundLabelType.Background : BackgroundLabelType.Label;

    // Fetch data
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await BackgroundLabelQuery.GetBackgroundLabelList({
                page: 1,
                pageSize: 1000, // Tüm itemları çek
                type: currentType,
                scope: BackgroundLabelScope.Global,
                searchTerm: searchTerm || undefined,
                category: filterCategory || undefined,
            });

            const data = response?.data || response;
            const allItems = data?.items || [];

            // Kategorilere göre grupla ve displayOrder'a göre sırala
            const grouped: { [key: string]: BackgroundLabelItem[] } = {};
            const uniqueCategories: string[] = [];

            allItems.forEach((item: BackgroundLabelItem) => {
                const category = item.category || t('general.uncategorized');
                if (!grouped[category]) {
                    grouped[category] = [];
                    uniqueCategories.push(category);
                }
                grouped[category].push(item);
            });

            // Her kategorideki itemları displayOrder'a göre sırala
            Object.keys(grouped).forEach((cat) => {
                grouped[cat].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            });

            setGroupedItems(grouped);
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Failed to fetch background/label list:', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentType, searchTerm, filterCategory, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // File selection handler (validates and stores file for preview)
    const handleFileSelect = (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const file = files[0];
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            Swal.fire({
                icon: 'error',
                title: t('studio.backgroundLabelManagement.upload.error'),
                text: t('studio.backgroundLabelManagement.upload.invalidType'),
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: t('studio.backgroundLabelManagement.upload.error'),
                text: t('studio.backgroundLabelManagement.upload.tooLarge'),
            });
            return;
        }

        setSelectedFile(file);
        setSelectedFilePreview(URL.createObjectURL(file));
        if (!uploadName) {
            setUploadName(file.name.replace(/\.[^/.]+$/, ''));
        }
    };

    // Upload handler (sends the selected file)
    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadProgress(30);

        try {
            const formData = new FormData();
            const fileName = uploadName || selectedFile.name.replace(/\.[^/.]+$/, '');
            const normalizedFileName = normalizeFileName(fileName);

            formData.append('File', selectedFile);
            formData.append('Name', normalizedFileName);
            formData.append('Type', currentType.toString());
            formData.append('Scope', BackgroundLabelScope.Global.toString());
            if (uploadCategory) {
                formData.append('Category', uploadCategory);
            }

            setUploadProgress(60);
            await BackgroundLabelCommand.UploadBackgroundLabel(formData);
            setUploadProgress(100);

            Swal.fire({
                icon: 'success',
                title: t('studio.backgroundLabelManagement.upload.success'),
                timer: 1500,
                showConfirmButton: false,
            });

            handleCloseUploadModal();
            fetchData();
        } catch (error) {
            console.error('Upload failed:', error);
            Swal.fire({
                icon: 'error',
                title: t('studio.backgroundLabelManagement.upload.error'),
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Modal open/close
    const handleOpenUploadModal = () => {
        setUploadModalOpen(true);
    };

    const handleCloseUploadModal = () => {
        setUploadModalOpen(false);
        setSelectedFile(null);
        if (selectedFilePreview) {
            URL.revokeObjectURL(selectedFilePreview);
        }
        setSelectedFilePreview(null);
        setUploadName('');
        setUploadCategory('');
        setUploadProgress(0);
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Delete handler
    const removeItemFromState = useCallback((item: BackgroundLabelItem) => {
        const category = item.category || t('general.uncategorized');

        setGroupedItems((prev) => {
            const updated = { ...prev };

            if (!updated[category]) {
                return prev;
            }

            updated[category] = updated[category].filter(
                (existing) => existing.id !== item.id && existing.s3Key !== item.s3Key
            );

            if (updated[category].length === 0) {
                delete updated[category];
            }

            setCategories(Object.keys(updated));
            return updated;
        });
    }, [t]);

    const handleDelete = async (item: BackgroundLabelItem) => {
        const result = await Swal.fire({
            title: t('studio.backgroundLabelManagement.grid.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.accept'),
            cancelButtonText: t('general.back'),
        });

        if (result.isConfirmed) {
            try {
                const parsedId = Number(item.id);
                await BackgroundLabelCommand.DeleteBackgroundLabel({
                    s3Key: item.s3Key,
                    id: Number.isFinite(parsedId) && parsedId > 0 ? parsedId : undefined,
                });

                removeItemFromState(item);

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    timer: 1500,
                    showConfirmButton: false,
                });

                fetchData();
            } catch (error) {
                console.error('Delete failed:', error);
                Swal.fire({ icon: 'error', title: t('general.error') });
                fetchData();
            }
        }
    };

    // Drag & drop
    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Türkçe karakterleri ve boşlukları düzeltir
    const normalizeFileName = (fileName: string): string => {
        const turkishCharMap: { [key: string]: string } = {
            ç: 'c',
            Ç: 'C',
            ğ: 'g',
            Ğ: 'G',
            ı: 'i',
            İ: 'I',
            ö: 'o',
            Ö: 'O',
            ş: 's',
            Ş: 'S',
            ü: 'u',
            Ü: 'U',
        };

        return fileName
            .split('')
            .map((char) => turkishCharMap[char] || char)
            .join('')
            .replaceAll(/\s+/g, '_') // Boşlukları alt çizgiye çevir
            .replaceAll(/[^\w.]/g, ''); // Özel karakterleri temizle
    };

    // Sürükle-bırak: Kategori içinde item'ları taşı
    const moveItem = useCallback((category: string, fromIndex: number, toIndex: number) => {
        setGroupedItems((prev) => {
            const updated = { ...prev };
            const categoryItems = [...(updated[category] || [])];
            const [moved] = categoryItems.splice(fromIndex, 1);
            categoryItems.splice(toIndex, 0, moved);
            updated[category] = categoryItems;
            return updated;
        });
    }, []);

    // Sürükle-bırak: Drop tamamlandığında backend'e kaydet
    const [isSortLoading, setIsSortLoading] = useState(false);

    const dropItem = useCallback(
        async (category: string) => {
            const categoryItems = groupedItems[category];
            if (!categoryItems || categoryItems.length === 0) return;

            const ids = categoryItems.map((item) => Number(item.id));

            setIsSortLoading(true);
            try {
                await BackgroundLabelCommand.UpdateBackgroundLabelDisplayOrder({
                    category,
                    type: currentType,
                    ids,
                });
                // Başarılı olursa veriyi yenile
                fetchData();
            } catch (error) {
                console.error('Failed to update display order:', error);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('studio.backgroundLabelManagement.displayOrder.error'),
                    timer: 2000,
                    showConfirmButton: false,
                });
                // Hata durumunda eski sıralamayı geri yükle
                fetchData();
            } finally {
                setIsSortLoading(false);
            }
        },
        [groupedItems, currentType, fetchData, t]
    );

    return (
        <SCUIContent>
            <SCUIBreadCrumb />
            <Box className="background-label-management">
                {/* Header */}
                <Box className="background-label-management__header">
                    <Typography variant="h5" fontWeight="bold">
                        {t('studio.backgroundLabelManagement.title')}
                    </Typography>
                </Box>

                {/* Tabs: Background / Labels */}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                    <Tab
                        icon={<Wallpaper />}
                        iconPosition="start"
                        label={t('studio.backgroundLabelManagement.tabs.backgrounds')}
                    />
                    <Tab
                        icon={<Label />}
                        iconPosition="start"
                        label={t('studio.backgroundLabelManagement.tabs.labels')}
                    />
                </Tabs>

                {/* Filters Row */}
                <Box className="background-label-management__filters">
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={categorySuggestions}
                        value={filterCategory}
                        onChange={(_, v) => setFilterCategory(v || '')}
                        onInputChange={(_, v) => setFilterCategory(v || '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t('studio.backgroundLabelManagement.category.filterPlaceholder')}
                                sx={{ minWidth: 180 }}
                            />
                        )}
                    />

                    <TextField
                        size="small"
                        placeholder={t('general.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{ minWidth: 220 }}
                    />

                    <SCUIButton
                        text={t('studio.backgroundLabelManagement.upload.button')}
                        type="primary"
                        sizeType="default"
                        size="m"
                        startIcon={<Add />}
                        onClick={handleOpenUploadModal}
                    />
                </Box>

                {/* Loading */}
                {(isLoading || isSortLoading) && <LinearProgress sx={{ mb: 2 }} />}

                {/* Kategorilere Göre Accordion */}
                {!isLoading && categories.length > 0 ? (
                    <Box sx={{ mb: 3 }}>
                        {categories.map((category) => {
                            const categoryItems = groupedItems[category] || [];
                            return (
                                <Accordion
                                    key={category}
                                    defaultExpanded={true}
                                    sx={{
                                        mb: 2,
                                        '&:before': { display: 'none' },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        borderRadius: '12px !important',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                            '& .MuiAccordionSummary-content': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                pr: 2,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" fontWeight="600">
                                                {category}
                                            </Typography>
                                            <Chip
                                                label={`${categoryItems.length} ${t('general.items')}`}
                                                size="small"
                                                color="primary"
                                                sx={{
                                                    height: 28,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('studio.backgroundLabelManagement.displayOrder.hint')}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 2 }}>
                                        <Grid container spacing={2}>
                                            {categoryItems.map((item, idx) => (
                                                <DraggableGridItem
                                                    key={item.id}
                                                    item={item}
                                                    index={idx}
                                                    category={category}
                                                    moveItem={moveItem}
                                                    dropItem={dropItem}
                                                    onDelete={handleDelete}
                                                    formatDate={formatDate}
                                                    formatFileSize={formatFileSize}
                                                />
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                ) : (
                    !isLoading && (
                        <Box className="background-label-management__empty-state">
                            <PhotoLibrary />
                            <Typography variant="body1">
                                {t('studio.backgroundLabelManagement.grid.noItems')}
                            </Typography>
                        </Box>
                    )
                )}

                {/* Upload Modal */}
                <Dialog
                    open={uploadModalOpen}
                    onClose={isUploading ? undefined : handleCloseUploadModal}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: { borderRadius: 3 },
                        },
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUpload color="primary" />
                            <Typography variant="h6">{t('studio.backgroundLabelManagement.upload.button')}</Typography>
                        </Box>
                        <IconButton size="small" onClick={handleCloseUploadModal} disabled={isUploading}>
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        {/* Drag & Drop Area */}
                        <Box
                            className={`background-label-management__upload-area ${
                                isDragging ? 'background-label-management__upload-area--dragging' : ''
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ mb: 0 }}
                        >
                            {selectedFilePreview ? (
                                <Box sx={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                                    <SCUIZoomableImage
                                        src={selectedFilePreview}
                                        alt={selectedFile?.name ?? ''}
                                        containerSx={{
                                            maxWidth: '100%',
                                            maxHeight: 200,
                                            borderRadius: 1,
                                        }}
                                        imageSx={{
                                            maxHeight: 200,
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                        }}
                                    />
                                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666' }}>
                                        {selectedFile?.name} ({selectedFile ? formatFileSize(selectedFile.size) : ''})
                                    </Typography>
                                    <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                                        {t('studio.backgroundLabelManagement.upload.changeFile')}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        {t('studio.backgroundLabelManagement.upload.dragDrop')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {t('studio.backgroundLabelManagement.upload.allowedTypes')}
                                    </Typography>
                                </>
                            )}
                        </Box>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileSelect(e.target.files)}
                        />

                        {/* Name */}
                        <TextField
                            size="small"
                            fullWidth
                            label={t('studio.backgroundLabelManagement.upload.nameLabel')}
                            placeholder={t('studio.backgroundLabelManagement.upload.namePlaceholder')}
                            value={uploadName}
                            onChange={(e) => setUploadName(e.target.value)}
                        />

                        {/* Category */}
                        <Autocomplete
                            freeSolo
                            size="small"
                            options={categorySuggestions}
                            value={uploadCategory}
                            onChange={(_, v) => setUploadCategory(v || '')}
                            onInputChange={(_, v) => setUploadCategory(v || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label={t('studio.backgroundLabelManagement.category.label')}
                                    placeholder={t('studio.backgroundLabelManagement.category.placeholder')}
                                />
                            )}
                        />

                        {/* Upload Progress */}
                        {isUploading && (
                            <Box>
                                <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 2 }} />
                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                    {t('studio.backgroundLabelManagement.upload.uploading')}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <SCUIButton
                            text={t('general.back')}
                            type="ghost"
                            sizeType="default"
                            size="m"
                            onClick={handleCloseUploadModal}
                            disabled={isUploading}
                        />
                        <SCUIButton
                            text={t('studio.backgroundLabelManagement.upload.button')}
                            type="primary"
                            sizeType="default"
                            size="m"
                            startIcon={<CloudUpload />}
                            onClick={handleUpload}
                            disabled={!selectedFile || isUploading}
                        />
                    </DialogActions>
                </Dialog>

            </Box>
        </SCUIContent>
    );
};

export default BackgroundLabelManagement;
